import React, { useState, useEffect, useCallback } from 'react';
import { apiService, handleApiError } from '../services/api';
import { useMeeting } from '../contexts/MeetingContext';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Safe toast utility with fallbacks
const safeToast = {
  success: (message) => toast.success(message),
  error: (message) => toast.error(message),
  info: (message) => {
    // Fallback to success if info is not available
    if (typeof toast.info === 'function') {
      toast.info(message);
    } else {
      toast.success(message);
    }
  }
};

// Stance pill component
const StancePill = ({ stance }) => {
  const getStanceClass = (stance) => {
    switch (stance?.toLowerCase()) {
      case 'supported':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'opposed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'mixed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'related':
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStanceClass(stance)}`}>
      {stance || 'Related'}
    </span>
  );
};

// Company pill component
const CompanyPill = ({ company }) => {
  if (!company) return null;
  
  // Shorten company names for display
  const getDisplayName = (company) => {
    // Handle common long names
    const shortNames = {
      'ZTE Corporation, Sanechips': 'ZTE',
      'Huawei, HiSilicon': 'Huawei',
      'Spreadtrum, UNISOC': 'UNISOC',
      'LG Electronics Inc.': 'LG',
      'NTT DOCOMO, INC.': 'NTT DOCOMO',
      'Qualcomm Incorporated': 'Qualcomm',
      'Google Ireland Limited': 'Google',
      'Transsion Holdings': 'Transsion',
      'InterDigital Inc.': 'InterDigital'
    };
    
    return shortNames[company] || company;
  };

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
      {getDisplayName(company)}
    </span>
  );
};

// Conclusion type badge component
const ConclusionTypeBadge = ({ conclusionType }) => {
  const getTypeClass = (type) => {
    switch (type?.toLowerCase()) {
      case 'agreement':
      case 'agreement_with_ffs':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'agreement_summary':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'captured':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'captured_scenario_and_deferred_to_wi':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'deferred_decision':
      case 'deferred_to_wi':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'noted':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  const getTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'agreement':
        return 'Agreed';
      case 'agreement_with_ffs':
        return 'Agreed (FFS)';
      case 'agreement_summary':
        return 'Summary';
      case 'captured':
        return 'Captured';
      case 'captured_scenario_and_deferred_to_wi':
        return 'Captured & Deferred';
      case 'deferred_decision':
        return 'Deferred';
      case 'deferred_to_wi':
        return 'WI Phase';
      case 'noted':
        return 'Noted';
      default:
        return type || 'Unknown';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTypeClass(conclusionType)}`}>
      {getTypeLabel(conclusionType)}
    </span>
  );
};

// Confidence badge component
const ConfidenceBadge = ({ confidence }) => {
  const getConfidenceClass = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high':
        return 'bg-green-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="flex items-center space-x-1">
      <div className={`w-2 h-2 rounded-full ${getConfidenceClass(confidence)}`}></div>
      <span className="text-xs text-gray-600">{confidence || 'Unknown'}</span>
    </div>
  );
};

// Proposal type badge
const ProposalTypeBadge = ({ type }) => {
  const getTypeClass = (type) => {
    switch (type?.toUpperCase()) {
      case 'REC':
        return 'bg-blue-100 text-blue-800';
      case 'DIS':
        return 'bg-orange-100 text-orange-800';
      case 'AGREED':
        return 'bg-green-100 text-green-800';
      case 'ACTION':
        return 'bg-red-100 text-red-800';
      case 'ASS':
        return 'bg-purple-100 text-purple-800';
      case 'OPT':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getTypeClass(type)}`}>
      {type || 'Unknown'}
    </span>
  );
};

// Document chip component (non-clickable)
const DocumentChip = ({ docId }) => (
  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-vodafone-100 text-vodafone-800">
    <DocumentTextIcon className="h-3 w-3 mr-1" />
    {docId}
  </span>
);

// Expandable conclusion row
const ConclusionRow = ({ conclusion, onExpand, isExpanded, selectedMeeting }) => {
  const handleRowClick = (e) => {
    // Don't expand if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    onExpand(conclusion.id);
  };

  return (
    <>
              <tr 
        className="hover:bg-gray-50 cursor-pointer border-b border-gray-200"
        onClick={handleRowClick}
      >
        <td className="px-4 py-3 w-1/2">
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-1">
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start space-x-2 mb-2">
                <ConclusionTypeBadge conclusionType={conclusion.conclusion_type} />
              </div>
              <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                {conclusion.conclusion_text}
              </p>
            </div>
          </div>
        </td>
        
        <td className="px-4 py-3 w-24">
          <div className="flex items-center space-x-2">
            <StancePill stance={conclusion.stance_summary} />
            {conclusion.has_conflicts && (
              <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" title="Has conflicting stances" />
            )}
          </div>
        </td>
        
        <td className="px-4 py-3 w-32">
          <div className="flex flex-wrap gap-1">
            {conclusion.unique_documents.slice(0, 3).map((docId) => (
              <DocumentChip 
                key={docId} 
                docId={docId} 
              />
            ))}
            {conclusion.unique_documents.length > 3 && (
              <span className="text-xs text-gray-500">
                +{conclusion.unique_documents.length - 3} more
              </span>
            )}
          </div>
        </td>
      </tr>
      
      {isExpanded && (
        <tr>
          <td colSpan="3" className="px-4 py-4 bg-gray-50">
                          <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">
                    Linked Proposals ({conclusion.proposal_links.length})
                  </h4>
                  <span className="text-xs text-gray-500">
                    From {selectedMeeting} Report Section: {conclusion.section_path}
                  </span>
                </div>
                

                <div className="overflow-x-auto">
                                  <table className="min-w-full">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase tracking-wider">
                      <th className="text-left py-2 w-32">Source Document</th>
                      <th className="text-left py-2 w-16">Type</th>
                      <th className="text-left py-2 w-20">Stance</th>
                      <th className="text-left py-2 w-1/2">Proposal Text</th>
                      <th className="text-left py-2">Linkage Reasoning</th>
                    </tr>
                  </thead>
                  <tbody className="space-y-2">
                    {conclusion.proposal_links.map((link, index) => (
                      <tr key={index} className="border-t border-gray-200">
                        <td className="py-2 w-32">
                          <DocumentChip docId={link.document_id} />
                        </td>
                        <td className="py-2 w-16">
                          <ProposalTypeBadge type={link.proposal_type} />
                        </td>
                        <td className="py-2 w-20">
                          <StancePill stance={link.stance} />
                        </td>
                        <td className="py-2 w-1/2">
                          <div className="space-y-2">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                              {link.proposal_text}
                            </p>
                            <CompanyPill company={link.company} />
                          </div>
                        </td>
                        <td className="py-2 text-sm text-gray-700">
                          <p className="whitespace-pre-wrap break-words leading-relaxed">
                            {link.match_reasoning || link.linkage_reason}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// Proposals table row
const ProposalRow = ({ proposal }) => {
  return (
    <tr className="hover:bg-gray-50 border-b border-gray-200">
      <td className="px-4 py-3 w-20">
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          {proposal.proposal_id}
        </span>
      </td>
      
      <td className="px-4 py-3 w-24">
        <DocumentChip docId={proposal.document_id} />
      </td>
      
      <td className="px-4 py-3 w-1/3">
        <div className="space-y-2">
          <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
            {proposal.proposal_text}
          </p>
          <CompanyPill company={proposal.company} />
        </div>
      </td>
      
      <td className="px-4 py-3 w-16">
        <ProposalTypeBadge type={proposal.proposal_type} />
      </td>
      
      <td className="px-4 py-3 w-1/4">
        <div className="space-y-2">
          {proposal.conclusions && proposal.conclusions.length > 0 ? (
            proposal.conclusions.map((conclusion, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <ConclusionTypeBadge conclusionType={conclusion.conclusion_type} />
                  <span className="text-xs text-gray-500">{conclusion.section_path}</span>
                </div>
                <p className="text-sm text-gray-900 leading-relaxed">
                  {conclusion.conclusion_text}
                </p>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center py-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                No conclusions matched
              </span>
            </div>
          )}
        </div>
      </td>
      
      <td className="px-4 py-3 w-1/4">
        <div className="space-y-2">
          {proposal.conclusions && proposal.conclusions.length > 0 ? (
            proposal.conclusions.map((conclusion, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <p className="text-sm text-gray-900 leading-relaxed">
                  {conclusion.linkage_reason || 'No linkage reason'}
                </p>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center py-4">
              <span className="text-sm text-gray-500 italic">No linkage reason</span>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// Main component
const CrossReferenceAnalysis = () => {
  // Meeting context
  const { selectedMeeting, selectedAgenda } = useMeeting();
  
  // State management
  const [activeTab, setActiveTab] = useState('proposals');
  const [conclusions, setConclusions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [proposalsBySubtopic, setProposalsBySubtopic] = useState({});
  const [subtopics, setSubtopics] = useState([]);
  const [, setDocumentIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filter state
  const [filters, setFilters] = useState({
    subtopic: '',
    search: '',
    confidence: '',
    doc_ids: '',
    page: 1,
    limit: 1000  // Increased to show all proposals without pagination
  });
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1000,  // Increased to match filters
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [subtopicsRes, docIdsRes] = await Promise.all([
          apiService.getCrossReferenceSubtopics(selectedMeeting, selectedAgenda),
          apiService.getCrossReferenceDocumentIds(selectedMeeting, selectedAgenda)
        ]);
        
        setSubtopics(subtopicsRes.data.subtopics || []);
        setDocumentIds(docIdsRes.data.document_ids || []);
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Failed to load filter options');
      }
    };
    
    loadInitialData();
  }, [selectedMeeting, selectedAgenda]);

  // Load table data
  const loadTableData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        ...filters,
        page: filters.page,
        limit: filters.limit
      };
      
      // Remove empty params
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      let response;
      if (activeTab === 'conclusions') {
        response = await apiService.getCrossReferenceConclusionsTable(selectedMeeting, selectedAgenda, params);
        setConclusions(response.data.conclusions || []);
      } else {
        response = await apiService.getCrossReferenceProposalsTable(selectedMeeting, selectedAgenda, params);
        setProposals(response.data.proposals || []);
        setProposalsBySubtopic(response.data.proposals_by_subtopic || {});
      }
      
      setPagination(response.data.pagination || {});
      
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to load data');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, selectedMeeting, selectedAgenda]);

  // Load data when filters or tab changes
  useEffect(() => {
    loadTableData();
  }, [loadTableData]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Handle row expansion
  const handleRowExpand = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  // Download functions
  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to download');
      return;
    }

    let csvContent = '';
    
    if (activeTab === 'conclusions') {
      // CSV headers for conclusions
      csvContent = 'Conclusion ID,Section Path,Conclusion Type,Conclusion Text,Linked Proposals Count,Linked Documents\n';
      
      data.forEach(conclusion => {
        const linkedDocs = conclusion.proposal_links?.map(link => link.document_id).join('; ') || '';
        const proposalCount = conclusion.proposal_links?.length || 0;
        
        csvContent += `"${conclusion.conclusion_id || ''}","${conclusion.section_path || ''}","${conclusion.conclusion_type || ''}","${(conclusion.conclusion_text || '').replace(/"/g, '""')}","${proposalCount}","${linkedDocs}"\n`;
      });
    } else {
      // CSV headers for proposals
      csvContent = 'Proposal ID,Document ID,Proposal Type,Proposal Text,Subtopic,Conclusions Count,Matched Conclusions,Linkage Reason\n';
      
      // Flatten proposals by subtopic
      Object.entries(proposalsBySubtopic).forEach(([subtopic, subtopicProposals]) => {
        subtopicProposals.forEach(proposal => {
          const conclusionsText = proposal.conclusions?.map(c => c.conclusion_text).join('; ') || 'No conclusions matched';
          // Get single linkage reason from first conclusion (since all conclusions for a proposal have the same linkage reason)
          const linkageReason = proposal.conclusions && proposal.conclusions.length > 0 
            ? proposal.conclusions[0].linkage_reason || 'No reason provided'
            : 'No linkage reason';
          const conclusionsCount = proposal.conclusions?.length || 0;
          
          csvContent += `"${proposal.proposal_id || ''}","${proposal.document_id || ''}","${proposal.proposal_type || ''}","${(proposal.proposal_text || '').replace(/"/g, '""')}","${subtopic}","${conclusionsCount}","${conclusionsText.replace(/"/g, '""')}","${linkageReason.replace(/"/g, '""')}"\n`;
        });
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    
    // Create filename suffix based on active filters
    const activeFilters = Object.entries(filters)
      .filter(([key, value]) => value && key !== 'page' && key !== 'limit')
      .map(([key, value]) => `${key}-${value.toString().replace(/[^a-zA-Z0-9]/g, '_')}`)
      .join('_');
    
    const filterSuffix = activeFilters ? `_filtered_${activeFilters}` : '';
    
    if (activeTab === 'conclusions') {
      const filename = `cross-reference-conclusions${filterSuffix}_${timestamp}.csv`;
      downloadCSV(conclusions, filename);
      safeToast.info(`Downloaded ${conclusions.length} conclusions (currently displayed data only)`);
    } else {
      const filename = `cross-reference-proposals${filterSuffix}_${timestamp}.csv`;
      // Convert proposalsBySubtopic to flat array for download
      const flatProposals = Object.entries(proposalsBySubtopic).flatMap(([subtopic, proposals]) => 
        proposals.map(p => ({ ...p, subtopic }))
      );
      downloadCSV(flatProposals, filename);
      const totalProposals = flatProposals.length;
      safeToast.info(`Downloaded ${totalProposals} proposals (currently displayed data only)`);
    }
  };

  // Handle export
  const handleExport = (format) => {
    if (format === 'csv') {
      // Create CSV data for Volkmar's requirement
      const csvData = [];
      
      if (activeTab === 'conclusions') {
        csvData.push([`${selectedMeeting} Conclusion ID`, 'Report Section', 'Conclusion Text', 'Linked Proposal Documents', 'Proposal Types', 'Linkage Reason']);
        
        // Group by linkage reason to avoid repetition
        conclusions.forEach(conclusion => {
          const groupedByReasoning = {};
          conclusion.proposal_links.forEach(link => {
            const key = link.linkage_reason;
            if (!groupedByReasoning[key]) {
              groupedByReasoning[key] = {
                linkage_reason: link.linkage_reason,
                documents: [],
                proposal_types: []
              };
            }
            groupedByReasoning[key].documents.push(link.document_id);
            groupedByReasoning[key].proposal_types.push(link.proposal_type);
          });

          Object.values(groupedByReasoning).forEach(group => {
            csvData.push([
              conclusion.conclusion_id,
              conclusion.section_path,
              `"${conclusion.conclusion_text.replace(/"/g, '""')}"`,
              [...new Set(group.documents)].join('; '),
              [...new Set(group.proposal_types)].join('; '),
              `"${group.linkage_reason.replace(/"/g, '""')}"`
            ]);
          });
        });
      } else {
        csvData.push(['Industry Document ID', 'Proposal Text', 'Proposal Type', `Linked ${selectedMeeting} Conclusions`, 'Report Sections']);
        
        proposals.forEach(proposal => {
          csvData.push([
            proposal.document_id,
            `"${proposal.proposal_text.replace(/"/g, '""')}"`,
            proposal.proposal_type,
            proposal.conclusions.map(c => c.conclusion_id).join('; '),
            proposal.report_sections.join('; ')
          ]);
        });
      }
      
      const csvContent = csvData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cross-reference-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${csvData.length - 1} cross-reference mappings to CSV`);
    } else {
      toast.success(`Export to ${format.toUpperCase()} - Feature coming soon!`);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      subtopic: '',
      search: '',
      confidence: '',
      doc_ids: '',
      page: 1,
      limit: 1000  // Increased to match default
    });
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button onClick={loadTableData} className="btn-vodafone">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Conclusion Analysis</h1>
          <p className="mt-2 text-gray-600">
            Cross-reference mapping between <strong>{selectedMeeting} Report conclusions</strong> and industry proposals for Agenda {selectedAgenda}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Showing which conclusions are linked to which proposals and their source documents
          </p>
        </div>
        
      </div>



      {/* Filters Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {/* Subtopic Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtopic</label>
            <select
              value={filters.subtopic}
              onChange={(e) => handleFilterChange('subtopic', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Subtopics</option>
              {subtopics.map((subtopic) => (
                <option key={subtopic} value={subtopic}>
                  {subtopic}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by text or company name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 text-sm"
              />
            </div>
          </div>





          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-gradient-to-r from-vodafone-50 to-blue-50 border border-vodafone-200 rounded-lg p-4">
        {filters.subtopic && (
          <div className="mb-3">
            <h3 className="text-sm font-medium text-gray-700">
              Statistics for: <span className="text-vodafone-600 font-semibold">{filters.subtopic}</span>
            </h3>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-vodafone-600">
              {pagination.total_count || 0}
            </div>
            <div className="text-sm text-gray-600">
              {activeTab === 'conclusions' ? `${selectedMeeting} Conclusions` : 'Industry Proposals'}
              {filters.subtopic && ' (filtered)'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {activeTab === 'conclusions' 
                ? conclusions.reduce((sum, c) => sum + c.proposal_count, 0)
                : proposals.reduce((sum, p) => sum + p.conclusion_count, 0)
              }
            </div>
            <div className="text-sm text-gray-600">
              Cross-Reference Links
              {filters.subtopic && ' (filtered)'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {activeTab === 'conclusions' 
                ? new Set(conclusions.flatMap(c => c.unique_documents)).size
                : new Set(proposals.map(p => p.document_id)).size
              }
            </div>
            <div className="text-sm text-gray-600">
              Source Documents
              {filters.subtopic && ' (filtered)'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {filters.subtopic ? 1 : subtopics.length}
            </div>
            <div className="text-sm text-gray-600">
              Technical Subtopics
              {filters.subtopic && ' (selected)'}
            </div>
          </div>
        </div>

      </div>

      

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('proposals')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'proposals'
                  ? 'border-vodafone-500 text-vodafone-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Industry Proposals → {selectedMeeting} Conclusions
            </button>
            <button
              onClick={() => setActiveTab('conclusions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'conclusions'
                  ? 'border-vodafone-500 text-vodafone-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {selectedMeeting} Conclusions → Industry Proposals
            </button>
          </nav>
          
          {/* Download Button */}
          <div className="flex items-center space-x-2 pb-2">
            <button
              onClick={handleDownload}
              disabled={loading || (activeTab === 'conclusions' && conclusions.length === 0) || (activeTab === 'proposals' && Object.keys(proposalsBySubtopic).length === 0)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vodafone-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
              Download CSV
            </button>
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="loading-spinner mr-3"></div>
            <span className="text-gray-600">Loading data...</span>
          </div>
        ) : (
          <>
            {/* Conclusions Table */}
            {activeTab === 'conclusions' && (
              <div>
                {filters.subtopic && (
                  <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      {filters.subtopic} ({conclusions.reduce((sum, c) => sum + c.proposal_count, 0)} linked proposals)
                    </h3>
                  </div>
                )}
                
                <div className="overflow-x-auto">
                  <table className="min-w-full table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/2">
                          Conclusion
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Stance Summary
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Documents
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {conclusions.map((conclusion) => (
                        <ConclusionRow
                          key={conclusion.id}
                          conclusion={conclusion}
                          onExpand={handleRowExpand}
                          isExpanded={expandedRows.has(conclusion.id)}
                          selectedMeeting={selectedMeeting}
                        />
                      ))}
                    </tbody>
                  </table>
                  
                  {conclusions.length === 0 && (
                    <div className="text-center py-12">
                      <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Conclusions Found</h3>
                      <p className="text-gray-600">Try adjusting your filters to see more results.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Proposals Table */}
            {activeTab === 'proposals' && (
              <div className="space-y-6">
                {Object.keys(proposalsBySubtopic).length > 0 ? (
                  Object.entries(proposalsBySubtopic).map(([subtopic, subtopicProposals]) => (
                    <div key={subtopic} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                          {subtopic} ({subtopicProposals.length} proposals)
                        </h3>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                Proposal ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                Doc ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                                Proposal Text
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                Type
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                                Matched {selectedMeeting} Conclusions
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                                Linkage Reason
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {subtopicProposals.map((proposal, index) => (
                              <ProposalRow
                                key={`${subtopic}-${proposal.proposal_id}-${index}`}
                                proposal={proposal}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Proposals Found</h3>
                    <p className="text-gray-600">Try adjusting your filters to see more results.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {(conclusions.length > 0 || proposals.length > 0) && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {((pagination.page - 1) * pagination.limit) + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total_count)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total_count}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.has_prev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.has_next}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossReferenceAnalysis; 